import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../utils/api';
import { KeystrokeEvent, Lesson } from '../../types';
import LoadingSpinner from '../ui/LoadingSpinner';

interface PlaybackModalProps {
    progressId: string;
    onClose: () => void;
}

const PlaybackModal: React.FC<PlaybackModalProps> = ({ progressId, onClose }) => {
    const [session, setSession] = useState<{ lesson: Lesson; log: KeystrokeEvent[] } | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [typedText, setTypedText] = useState("");
    const [isIncorrect, setIsIncorrect] = useState(false);

    const timerRef = useRef<number | null>(null);

    useEffect(() => {
        const fetchSession = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await api.get<{ lesson: Lesson, keystroke_log: KeystrokeEvent[] }>(`/api/v1/sessions/${progressId}`);
                setSession({ lesson: data.lesson, log: data.keystroke_log });
            } catch (err) {
                if (err instanceof Error) setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchSession();
    }, [progressId]);

    const stopPlayback = () => {
        setIsPlaying(false);
        if (timerRef.current) clearTimeout(timerRef.current);
    };
    
    const resetPlayback = () => {
        stopPlayback();
        setCurrentIndex(0);
        setTypedText("");
        setIsIncorrect(false);
    };

    useEffect(() => {
        return () => stopPlayback(); // Cleanup on unmount
    }, []);

    useEffect(() => {
        if (!isPlaying || !session || currentIndex >= session.log.length) {
            setIsPlaying(false);
            return;
        }

        const currentEvent = session.log[currentIndex];
        const nextEvent = session.log[currentIndex + 1];

        if (currentEvent.type === 'correct') {
            setTypedText(text => text + currentEvent.key);
            setIsIncorrect(false);
        } else if (currentEvent.type === 'incorrect') {
            setIsIncorrect(true);
            setTimeout(() => setIsIncorrect(false), 200 / playbackSpeed);
        }

        if (nextEvent) {
            const delay = (nextEvent.time - currentEvent.time) / playbackSpeed;
            timerRef.current = window.setTimeout(() => {
                setCurrentIndex(i => i + 1);
            }, delay);
        } else {
            setIsPlaying(false); // End of log
        }

    }, [isPlaying, currentIndex, session, playbackSpeed]);

    const renderLessonText = () => {
        if (!session) return null;
        return session.lesson.lesson_text.split("").map((char, index) => {
            let charClass = "text-gray-500";
            if (index < typedText.length) {
                charClass = "text-green-600";
            } else if (index === typedText.length) {
                charClass = isIncorrect ? "text-red-500 bg-red-100" : "text-blue-600 border-b-2 border-blue-600";
            }
            if (char === ' ') charClass += " px-1";
            return <span key={index} className={`font-mono text-2xl md:text-3xl ${charClass}`}>{char === ' ' ? '\u00A0' : char}</span>;
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4 border-b pb-4">
                    <h2 className="text-2xl font-bold text-gray-800">Typing Playback</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-3xl">&times;</button>
                </div>
                {loading && <LoadingSpinner message="Loading session..." />}
                {error && <p className="text-red-500 text-center">{error}</p>}
                {session && (
                    <div className="flex-grow flex flex-col">
                        <h3 className="font-semibold text-lg text-gray-900 mb-4">{session.lesson.title}</h3>
                        <div className="p-4 md:p-6 mb-6 bg-gray-100 rounded-lg overflow-x-auto whitespace-nowrap leading-relaxed flex-grow">
                            {renderLessonText()}
                        </div>
                        <div className="flex items-center justify-center space-x-4">
                            <button onClick={() => setIsPlaying(p => !p)} className="px-4 py-2 bg-blue-600 text-white rounded-md w-24">
                                {isPlaying ? 'Pause' : 'Play'}
                            </button>
                            <button onClick={resetPlayback} className="px-4 py-2 bg-gray-500 text-white rounded-md">Reset</button>
                            <div className="flex items-center space-x-2">
                                <label htmlFor="speed">Speed:</label>
                                <select id="speed" value={playbackSpeed} onChange={e => setPlaybackSpeed(Number(e.target.value))} className="p-2 border border-gray-300 rounded-md bg-white">
                                    <option value={0.5}>0.5x</option>
                                    <option value={1}>1x</option>
                                    <option value={2}>2x</option>
                                    <option value={4}>4x</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlaybackModal;